import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { paymentsService } from '../../services';
import './PaymentsList.css';
import './PaymentsList-Mobile.css';
import SecurePaymentDetailsModal from './SecurePaymentDetailsModal';
import './PaymentsList.css';

const PaymentsList = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showSecureModal, setShowSecureModal] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await paymentsService.getAll();
      setPayments(data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setError('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setShowSecureModal(true);
  };

  const closeSecureModal = () => {
    setShowSecureModal(false);
    setSelectedPayment(null);
  };

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.quote_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.confirmation_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'badge badge-warning',
      completed: 'badge badge-success',
      failed: 'badge badge-danger',
      refunded: 'badge badge-secondary'
    };
    return statusClasses[status] || 'badge badge-gray';
  };

  const getPaymentTypeBadge = (type) => {
    const typeClasses = {
      deposit: 'badge badge-blue',
      partial: 'badge badge-orange',
      final: 'badge badge-purple',
      full: 'badge badge-green'
    };
    return typeClasses[type] || 'badge badge-gray';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading payments...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="payments-list">
      <div className="page-header">
        <h1>Client Payments</h1>
        <p className="page-description">
          View and manage payment information for your quotes and itineraries. 
          Click "View Details" to securely access sensitive payment data.
        </p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by client name, quote number, or confirmation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Payments</h3>
          <div className="summary-value">{filteredPayments.length}</div>
        </div>
        <div className="summary-card">
          <h3>Completed</h3>
          <div className="summary-value">
            {filteredPayments.filter(p => p.status === 'completed').length}
          </div>
        </div>
        <div className="summary-card">
          <h3>Total Amount</h3>
          <div className="summary-value">
            ${filteredPayments
              .filter(p => p.status === 'completed')
              .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
              .toFixed(2)}
          </div>
        </div>
        <div className="summary-card">
          <h3>This Month</h3>
          <div className="summary-value">
            {filteredPayments.filter(p => {
              const paymentDate = new Date(p.created_at);
              const now = new Date();
              return paymentDate.getMonth() === now.getMonth() && 
                     paymentDate.getFullYear() === now.getFullYear();
            }).length}
          </div>
        </div>
      </div>

      {/* Payments Table */}
      {filteredPayments.length === 0 ? (
        <div className="no-data">
          <p>No payments found matching your criteria.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="payments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Quote/Booking</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Status</th>
                <th>Confirmation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <tr key={payment.id}>
                  <td>{formatDate(payment.created_at)}</td>
                  <td>
                    <div className="client-info">
                      <strong>{payment.first_name} {payment.last_name}</strong>
                    </div>
                  </td>
                  <td>
                    <div className="quote-info">
                      <strong>{payment.quote_title}</strong>
                      <small>#{payment.quote_number}</small>
                    </div>
                  </td>
                  <td>
                    <span className="amount">${parseFloat(payment.amount || 0).toFixed(2)}</span>
                  </td>
                  <td>
                    <span className={getPaymentTypeBadge(payment.payment_type)}>
                      {payment.payment_type?.replace('_', ' ') || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={getStatusBadge(payment.status)}>
                      {payment.status}
                    </span>
                  </td>
                  <td>
                    <code className="confirmation-code">
                      {payment.confirmation_number}
                    </code>
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleViewPaymentDetails(payment)}
                        title="View secure payment details (requires 2FA)"
                      >
                        🔒 View Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Secure Payment Details Modal */}
      {showSecureModal && selectedPayment && (
        <SecurePaymentDetailsModal
          payment={selectedPayment}
          onClose={closeSecureModal}
        />
      )}
    </div>
  );
};

export default PaymentsList;
